// This file is auto-generated by @hey-api/openapi-ts

import type { Client, ClientMeta, Options as Options2, RequestResult, TDataShape } from './client';
import { client } from './client.gen';
import type { CloseSessionData, CloseSessionErrors, CloseSessionResponses, LatestData, LatestErrors, LatestResponses, RegisterSessionData, RegisterSessionErrors, RegisterSessionResponses, ResolvePageData, ResolvePageErrors, ResolvePageResponses, SeriesData, SeriesErrors, SeriesResponses, SessionStatusData, SessionStatusErrors, SessionStatusResponses, SubmitRequirementsData, SubmitRequirementsErrors, SubmitRequirementsResponses } from './types.gen';

export type Options<TData extends TDataShape = TDataShape, ThrowOnError extends boolean = boolean, TResponse = unknown> = Options2<TData, ThrowOnError, TResponse> & {
    /**
     * You can provide a client instance returned by `createClient()` instead of
     * individual options. This might be also useful if you want to implement a
     * custom client.
     */
    client?: Client;
    /**
     * You can pass arbitrary values through the `meta` object. This can be
     * used to access values that aren't defined as part of the SDK function.
     */
    meta?: keyof ClientMeta extends never ? Record<string, unknown> : ClientMeta;
};

/**
 * `POST /api/v1/data-sessions` — register a data session for the
 * authenticated account (spec §3.1). The session id is a cryptographically
 * random UUID bound to the account; it is routing identity, never a bearer
 * secret (§3.2).
 */
export const registerSession = <ThrowOnError extends boolean = false>(options?: Options<RegisterSessionData, ThrowOnError>): RequestResult<RegisterSessionResponses, RegisterSessionErrors, ThrowOnError> => (options?.client ?? client).post<RegisterSessionResponses, RegisterSessionErrors, ThrowOnError>({ url: '/api/v1/data-sessions', ...options });

/**
 * `DELETE /api/v1/data-sessions/{sessionId}` — explicit close (spec §3.3).
 *
 * **Design choice:** close = DELETE the session row; the foreign key
 * cascade removes the acked requirement set with it. The session id never
 * comes back (recovery is a NEW session — reconnect semantics re-POST the
 * desired set there per DR-1), so there is no CLOSED-state residue to
 * garbage-collect. Backend work that exists only for the session is torn
 * down with it.
 */
export const closeSession = <ThrowOnError extends boolean = false>(options: Options<CloseSessionData, ThrowOnError>): RequestResult<CloseSessionResponses, CloseSessionErrors, ThrowOnError> => (options.client ?? client).delete<CloseSessionResponses, CloseSessionErrors, ThrowOnError>({ url: '/api/v1/data-sessions/{sessionId}', ...options });

/**
 * `POST /api/v1/data-sessions/{sessionId}/requirements` — submit the
 * COMPLETE desired requirement set at a monotonically increasing revision
 * (spec §7, DR-1).
 *
 * Semantics: highest revision wins (CAS in one transaction across both
 * revision counters, see [`repo::submit_requirements`]); a stale or
 * duplicate revision is an idempotent no-op acked with the currently
 * accepted revision (`acceptedRevision`), so the client can simply stop
 * re-sending. The ack is returned only after the commit; replanning and the
 * requirement-bus broadcast fire asynchronously afterwards.
 */
export const submitRequirements = <ThrowOnError extends boolean = false>(options: Options<SubmitRequirementsData, ThrowOnError>): RequestResult<SubmitRequirementsResponses, SubmitRequirementsErrors, ThrowOnError> => (options.client ?? client).post<SubmitRequirementsResponses, SubmitRequirementsErrors, ThrowOnError>({
    url: '/api/v1/data-sessions/{sessionId}/requirements',
    ...options,
    headers: {
        'Content-Type': 'application/json',
        ...options.headers
    }
});

/**
 * `GET /api/v1/data-sessions/{sessionId}/status` — light operational status
 * (spec §35 "optional operational endpoint"). Also refreshes the session's
 * liveness timestamp (every authenticated op is a heartbeat).
 */
export const sessionStatus = <ThrowOnError extends boolean = false>(options: Options<SessionStatusData, ThrowOnError>): RequestResult<SessionStatusResponses, SessionStatusErrors, ThrowOnError> => (options.client ?? client).get<SessionStatusResponses, SessionStatusErrors, ThrowOnError>({ url: '/api/v1/data-sessions/{sessionId}/status', ...options });

/**
 * `GET /api/v1/data/latest` — deterministic synthetic latest quote.
 */
export const latest = <ThrowOnError extends boolean = false>(options: Options<LatestData, ThrowOnError>): RequestResult<LatestResponses, LatestErrors, ThrowOnError> => (options.client ?? client).get<LatestResponses, LatestErrors, ThrowOnError>({ url: '/api/v1/data/latest', ...options });

/**
 * `GET /api/v1/data/series` — deterministic synthetic OHLCV series.
 */
export const series = <ThrowOnError extends boolean = false>(options: Options<SeriesData, ThrowOnError>): RequestResult<SeriesResponses, SeriesErrors, ThrowOnError> => (options.client ?? client).get<SeriesResponses, SeriesErrors, ThrowOnError>({ url: '/api/v1/data/series', ...options });

/**
 * `GET /api/v1/page` — resolve a route to a `PageManifest`.
 *
 * Capability negotiation: a client advertising a runtime version below a template's
 * `compatibility.minRuntimeVersion` is downgraded to a compatible template. (Widget-type
 * advertisement is parsed but not yet enforced — a real gate ships together with
 * `required_capabilities`.)
 */
export const resolvePage = <ThrowOnError extends boolean = false>(options: Options<ResolvePageData, ThrowOnError>): RequestResult<ResolvePageResponses, ResolvePageErrors, ThrowOnError> => (options.client ?? client).get<ResolvePageResponses, ResolvePageErrors, ThrowOnError>({ url: '/api/v1/page', ...options });
